import React from 'react';
import { Check, CreditCard, Zap, Shield, Star, Rocket } from 'lucide-react';
import { useBusinessContext } from '../../contexts/BusinessContext';

const PlanSelectionStep = ({ 
  plans, 
  selectedPlan, 
  billingInterval, 
  onBillingIntervalChange, 
  onPlanSelect,
  preselectedPlan 
}) => {
  const { isFoundingMember } = useBusinessContext();

  // Handle both array and object with plans property
  const plansArray = Array.isArray(plans) ? plans : (plans?.plans || []);
  
  const soloPlan = plansArray.find(p => p.name?.toLowerCase() === 'solo' || p.tier === 'Solo');
  const proPlan = plansArray.find(p => p.name?.toLowerCase() === 'professional' || p.name?.toLowerCase() === 'pro' || p.tier === 'Pro');
  const enterprisePlan = plansArray.find(p => p.name?.toLowerCase() === 'enterprise' || p.tier === 'Enterprise');

  // Founder pricing applies only to Pro + monthly
  const founderRate = 10.00;

  const getPrice = (plan) => {
    if (!plan) return null;
    const isPro = plan === proPlan;
    const isApplicable = isFoundingMember && isPro && billingInterval === 'monthly';
    if (isApplicable) return founderRate;
    return billingInterval === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
  };

  const getStandardPrice = (plan) => {
    if (!plan) return null;
    return billingInterval === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
  };

  const isFounderPriceForPlan = (plan) => {
    if (!plan || plan !== proPlan) return false;
    return isFoundingMember && billingInterval === 'monthly';
  };

  const getSavings = (plan) => {
    if (!plan || billingInterval === 'monthly') return null;
    const monthlyTotal = plan.monthlyPrice * 12;
    const annualPrice = plan.annualPrice;
    return monthlyTotal - annualPrice;
  };

  const isSelected = (plan) => {
    if (!plan) return false;
    const planName = plan.name?.toLowerCase() || plan.tier?.toLowerCase();
    return preselectedPlan?.toLowerCase() === planName || selectedPlan?.id === plan.id;
  };

  // When founder enters this page with ?plan=pro, they should be locked to monthly
  // (since founder pricing is monthly-only). We just hide the annual toggle.
  const showBillingToggle = !isFoundingMember;

  return (
    <div className="animate-fade-in">
      
      {/* Founder Banner */}
      {isFoundingMember && (
        <div className="max-w-4xl mx-auto mb-8 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="bg-amber-500 rounded-full p-2 flex-shrink-0">
              <Star className="h-5 w-5 text-white fill-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-amber-900 mb-1">
                Welcome, Founding Member
              </h3>
              <p className="text-sm text-amber-800">
                Your Pro plan is locked at <strong>$10/month for life</strong>. This rate will never increase.
                Founder pricing is monthly-only; Solo and Enterprise are available at standard prices if you prefer.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Choose Your Plan
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          From solo operator to enterprise team—we have a plan for you. All plans include a{' '}
          <span className="font-semibold text-blue-600">14-day free trial</span>.
        </p>
      </div>

      {/* Billing Interval Toggle - hidden for founders (monthly-only) */}
      {showBillingToggle && (
        <div className="flex justify-center mb-10">
          <div className="bg-white rounded-xl p-1.5 shadow-md inline-flex border border-gray-200">
            <button
              onClick={() => onBillingIntervalChange('monthly')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                billingInterval === 'monthly'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => onBillingIntervalChange('annual')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all relative ${
                billingInterval === 'annual'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="absolute -top-3 -right-3 bg-green-500 text-white text-xs px-2.5 py-1 rounded-full font-bold shadow-lg">
                1 Month Free
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Pricing Cards - 3 Tier */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
        
        {/* Solo Plan */}
        {soloPlan && (
          <div className={`relative bg-white border-2 rounded-2xl p-8 transition-all hover:shadow-xl ${
            isSelected(soloPlan)
              ? 'border-blue-500 shadow-lg scale-[1.02]' 
              : 'border-gray-200'
          }`}>
            
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-5 w-5 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-900">Solo</h3>
              </div>
              
              <div className="mb-2">
                <span className="text-5xl font-bold text-gray-900">
                  ${getPrice(soloPlan)}
                </span>
                <span className="text-gray-600 text-lg">
                  /{billingInterval === 'monthly' ? 'month' : 'year'}
                </span>
              </div>
              
              {billingInterval === 'annual' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-600 font-semibold">
                    💰 Save ${getSavings(soloPlan)}/year
                  </span>
                </div>
              )}
              
              <p className="text-gray-600 mt-3">
                Perfect for solo operators and side hustles
              </p>
            </div>
            
            <ul className="space-y-3.5 mb-8">
              {(soloPlan.features || [
                'Unlimited customers',
                'Job & appointment tracking',
                'Professional invoicing',
                'SuddySnap™ receipt scanner',
                'Schedule C tax reports',
                'Essential email notifications',
                'Mobile optimized'
              ]).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
              <li className="flex items-start gap-3 text-gray-400">
                <span className="h-5 w-5 flex-shrink-0 mt-0.5 text-center">—</span>
                <span>Single user only</span>
              </li>
            </ul>
            
            <button
              onClick={() => onPlanSelect(soloPlan)}
              className="w-full bg-gray-900 text-white py-4 rounded-xl text-lg font-semibold hover:bg-gray-800 transition-all flex items-center justify-center gap-2 group"
            >
              <CreditCard className="h-5 w-5" />
              Choose Solo
            </button>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              Start your 14-day free trial
            </p>
          </div>
        )}

        {/* Professional Plan - Featured */}
        {proPlan && (
          <div className={`relative rounded-2xl p-8 shadow-2xl transform md:scale-[1.05] ${
            isFounderPriceForPlan(proPlan)
              ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white'
              : 'bg-gradient-to-br from-blue-600 to-blue-700 text-white'
          }`}>
            
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              {isFounderPriceForPlan(proPlan) ? (
                <div className="bg-white text-amber-700 px-5 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-1.5 border-2 border-amber-300">
                  <Star className="h-4 w-4 fill-current" />
                  FOUNDER RATE
                </div>
              ) : (
                <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-5 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-1.5">
                  <Star className="h-4 w-4 fill-current" />
                  MOST POPULAR
                </div>
              )}
            </div>
            
            <div className="mb-6 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <Zap className={`h-6 w-6 ${isFounderPriceForPlan(proPlan) ? 'text-white' : 'text-yellow-300'}`} />
                <h3 className="text-2xl font-bold">{proPlan.name || 'Professional'}</h3>
              </div>
              
              {isFounderPriceForPlan(proPlan) ? (
                <>
                  <div className="flex items-baseline gap-3 mb-1">
                    <span className="text-2xl text-amber-200 line-through font-semibold">
                      ${getStandardPrice(proPlan)}
                    </span>
                    <span className="text-5xl font-bold">
                      ${founderRate}
                    </span>
                    <span className="text-amber-100 text-lg">/month</span>
                  </div>
                  <p className="text-sm font-semibold mt-2 flex items-center gap-1">
                    🔒 Locked for life — will never increase
                  </p>
                </>
              ) : (
                <>
                  <div className="mb-2">
                    <span className="text-5xl font-bold">
                      ${getPrice(proPlan)}
                    </span>
                    <span className="text-blue-100 text-lg">
                      /{billingInterval === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  
                  {billingInterval === 'annual' && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-yellow-300 font-semibold">
                        💰 Save ${getSavings(proPlan)}/year
                      </span>
                    </div>
                  )}
                </>
              )}
              
              <p className={`mt-3 ${isFounderPriceForPlan(proPlan) ? 'text-amber-50' : 'text-blue-100'}`}>
                Everything you need to manage and grow your team
              </p>
            </div>
            
            <ul className="space-y-3.5 mb-8">
              {(proPlan.features || [
                'Everything in Solo +',
                'Up to 15 team members',
                'P&L financial reports',
                'Route optimization',
                'Automated emails & reminders',
                'Customer portal',
                'Priority support'
              ])
                .filter(feature => !feature.toLowerCase().includes('quickbooks'))
                .map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                      isFounderPriceForPlan(proPlan) ? 'text-white' : 'text-yellow-300'
                    }`} />
                    <span>{feature}</span>
                  </li>
                ))}
            </ul>
            
            <button
              onClick={() => onPlanSelect(proPlan)}
              className={`w-full py-4 rounded-xl text-lg font-bold transition-all flex items-center justify-center gap-2 shadow-lg group ${
                isFounderPriceForPlan(proPlan)
                  ? 'bg-white text-amber-700 hover:bg-amber-50'
                  : 'bg-white text-blue-600 hover:bg-blue-50'
              }`}
            >
              <CreditCard className="h-5 w-5" />
              {isFounderPriceForPlan(proPlan) ? 'Activate Founder Rate' : `Choose ${proPlan.name || 'Professional'}`}
            </button>
            
            <p className={`text-center text-sm mt-4 ${
              isFounderPriceForPlan(proPlan) ? 'text-amber-100' : 'text-blue-100'
            }`}>
              {isFounderPriceForPlan(proPlan)
                ? '14-day free trial • Then $10/mo forever'
                : 'Start your 14-day free trial'}
            </p>
          </div>
        )}

        {/* Enterprise Plan */}
        {enterprisePlan && (
          <div className={`relative bg-white border-2 rounded-2xl p-8 transition-all hover:shadow-xl ${
            isSelected(enterprisePlan)
              ? 'border-purple-500 shadow-lg scale-[1.02]' 
              : 'border-purple-200'
          }`}>
            
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Rocket className="h-5 w-5 text-purple-600" />
                <h3 className="text-2xl font-bold text-gray-900">Enterprise</h3>
              </div>
              
              <div className="mb-2">
                <span className="text-5xl font-bold text-gray-900">
                  ${getPrice(enterprisePlan)}
                </span>
                <span className="text-gray-600 text-lg">
                  /{billingInterval === 'monthly' ? 'month' : 'year'}
                </span>
              </div>
              
              {billingInterval === 'annual' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-600 font-semibold">
                    💰 Save ${getSavings(enterprisePlan)}/year
                  </span>
                </div>
              )}
              
              <p className="text-gray-600 mt-3">
                Advanced features for growing businesses
              </p>
            </div>
            
            <ul className="space-y-3.5 mb-8">
              {(enterprisePlan.features || [
                'Everything in Professional +',
                'Unlimited team members',
                'API access',
                'Advanced analytics',
                'Multi-location support',
                'Custom branding',
                'Re-engagement campaigns',
                'Custom email templates'
              ]).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button
              onClick={() => onPlanSelect(enterprisePlan)}
              className="w-full bg-purple-600 text-white py-4 rounded-xl text-lg font-semibold hover:bg-purple-700 transition-all flex items-center justify-center gap-2 group"
            >
              <CreditCard className="h-5 w-5" />
              Choose Enterprise
            </button>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              Start your 14-day free trial
            </p>
          </div>
        )}
      </div>

      {/* Feature Comparison */}
      <div className="max-w-4xl mx-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
          Every plan includes:
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="text-4xl mb-4">🔒</div>
            <h4 className="font-semibold text-gray-900 mb-2 text-lg">Bank-Level Security</h4>
            <p className="text-gray-600 text-sm">
              Your data is encrypted and secure. PCI compliant payment processing.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="text-4xl mb-4">📱</div>
            <h4 className="font-semibold text-gray-900 mb-2 text-lg">Works Everywhere</h4>
            <p className="text-gray-600 text-sm">
              Desktop, tablet, or mobile. Manage your business from anywhere.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="text-4xl mb-4">🇺🇸</div>
            <h4 className="font-semibold text-gray-900 mb-2 text-lg">Veteran Owned</h4>
            <p className="text-gray-600 text-sm">
              Built by veterans for service businesses. Support that understands your needs.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PlanSelectionStep;